// value is based on Date.getDay()
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/getDay
import { Hours } from '@zcode/bizos'
import { addDays, addMonths, isBefore, isValid, startOfMonth } from 'date-fns'
import { zonedTimeToUtc } from 'date-fns-tz'
import { oisEmpty } from '../../shared/utils/utils'

export const SUNDAY = { value: 0, key: 'sun', title: 'Sunday' } as const
export const MONDAY = { value: 1, key: 'mon', title: 'Monday' } as const
export const TUESDAY = { value: 2, key: 'tue', title: 'Tuesday' } as const
export const WEDNESDAY = { value: 3, key: 'wed', title: 'Wednesday' } as const
export const THURSDAY = { value: 4, key: 'thu', title: 'Thursday' } as const
export const FRIDAY = { value: 5, key: 'fri', title: 'Friday' } as const
export const SATURDAY = { value: 6, key: 'sat', title: 'Saturday' } as const

export type Day =
  | typeof SUNDAY
  | typeof MONDAY
  | typeof TUESDAY
  | typeof WEDNESDAY
  | typeof THURSDAY
  | typeof FRIDAY
  | typeof SATURDAY

export function getDay(value: number, propKey?: 'value'): Day | null
export function getDay(value: Day['value'], propKey?: 'value'): Day
export function getDay<Key extends Exclude<keyof Day, 'value'>>(value: Day[Key], propKey: Key): Day
export function getDay<Key extends Exclude<keyof Day, 'value'>>(
  value: string,
  propKey: Key
): Day | null
export function getDay<Key extends keyof Day>(value: Day[Key], propKey: Key): Day
export function getDay<Key extends keyof Day>(
  value: Key extends 'value' ? number : string,
  propKey: Key
): Day | null
export function getDay<Key extends keyof Day>(
  value: Key extends 'value' ? number : Day[Key],
  // @ts-expect-error "value" might not match Key
  propKey: Key = 'value'
) {
  switch (value) {
    case SUNDAY[propKey]:
      return SUNDAY
    case MONDAY[propKey]:
      return MONDAY
    case TUESDAY[propKey]:
      return TUESDAY
    case WEDNESDAY[propKey]:
      return WEDNESDAY
    case THURSDAY[propKey]:
      return THURSDAY
    case FRIDAY[propKey]:
      return FRIDAY
    case SATURDAY[propKey]:
      return SATURDAY
    default:
      return null
  }
}

/**
 * iterates through Hours and finds todays schedule
 *
 * @param hours
 * @param today
 */
export const getTodaySchedule = (
  hours: readonly Hours[] | null | undefined,
  today: ReturnType<typeof getDay>
) => {
  if (!Hours.isNonEmptyHours(hours) || oisEmpty(today)) {
    return null
  }
  const day = hours.find((day) => day && day.day === today.key)
  if (!Hours.isValidHours(day)) {
    return null
  }
  return day
}

/**
 * gives a date as a string in '[Month] [date], [full year]' form
 *
 * @param date
 */
export function getDateText(date: Date): `${string} ${number}, ${number}`
export function getDateText(): ''
export function getDateText(date?: Date): '' | `${string} ${number}, ${number}`
export function getDateText(date?: Date) {
  if (date) {
    const month = date.toLocaleString('en-us', { month: 'long' })
    return `${month} ${date.getDate()}, ${date.getFullYear()}` as const
  }
  return ''
}

export const parseEpoch = (epoch: number) => {
  if (epoch) {
    const date = new Date(epoch * 1000)
    if (isValid(date)) {
      return date
    }
  }
  return null
}

/*  @param {string} isoString - an ISO 8601 format date and time string
 **                             e.g. 2015-11-24T19:40:00, optional time
 **  @returns {Date} - Date instance from parsing the string. May be NaN.
 */
export const parseISOLocal = (isoString: string) => {
  const parts = isoString.split(/\D/)
  return new Date(
    Number(parts[0]),
    Number(parts[1]) - 1,
    Number(parts[2]),
    parts[3] ? Number(parts[3]) : 0,
    parts[4] ? Number(parts[4]) : 0,
    parts[5] ? Number(parts[5]) : 0
  )
}

export const localizeDateInTimezone = (date: Date, tz: string) => {
  // utcToZonedTime (which you might expect to be the inverse of zonedTimeToUtc) often gives the
  // right answer here, but not always: if date is 2020-11-01T03:59:59.000Z, tz is America/New_York,
  // and the browser's timezone is also Eastern time, utcToZonedTime incorrectly produces
  // 2020-11-01T04:59:59.000Z (the right answer is the same as the input in this case).
  const wrongWay = zonedTimeToUtc(date, tz)
  const time = date.getTime()
  const diff = time - wrongWay.getTime()
  return new Date(time + diff)
}
